import { useMemo } from 'react';
import type { LiveEmployee } from '@geotracker/contracts';
import { StatTile } from '@/components/ui/card';
import { formatDistance, formatDuration } from '@/lib/format';
import { needsAttention } from './roster';

const STALE_SECONDS = 300;

/**
 * The six numbers an operations manager checks before looking at anything else.
 *
 * Each tile carries a second line, because the headline figure on its own is
 * usually the wrong question: "4 stationary" matters far less than "4
 * stationary, longest 2h 10m", and "12 reporting" means nothing without the
 * roster size it is out of.
 */
export function FleetSummary({ employees }: { employees: LiveEmployee[] }) {
  const stats = useMemo(() => {
    const reporting = employees.filter(
      (e) => e.position && e.position.ageSeconds <= STALE_SECONDS,
    );
    const moving = employees.filter((e) => e.movement.state === 'moving');
    const stationary = employees.filter((e) => e.movement.state === 'stationary');
    const dwells = employees
      .map((e) => e.dwell?.seconds ?? 0)
      .filter((s) => s > 0)
      .sort((a, b) => b - a);
    const attention = employees.filter(needsAttention);
    const distance = employees.reduce((sum, e) => sum + e.today.distanceMeters, 0);
    const tracking = employees.filter(
      (e) => e.state.sessionStatus === 'active' || e.state.sessionStatus === 'stale',
    );

    return {
      total: employees.length,
      reporting: reporting.length,
      moving: moving.length,
      stationary: stationary.length,
      longestDwell: dwells[0] ?? null,
      attention: attention.length,
      distance,
      tracking: tracking.length,
      fastest: moving.reduce<number | null>(
        (max, e) => (e.movement.speedMps === null ? max : Math.max(max ?? 0, e.movement.speedMps)),
        null,
      ),
    };
  }, [employees]);

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <StatTile
        label="Reporting"
        value={`${stats.reporting}/${stats.total}`}
        sub={`${stats.tracking} shift${stats.tracking === 1 ? '' : 's'} open`}
      />
      <StatTile
        label="Moving"
        value={stats.moving}
        tone="moving"
        sub={
          stats.fastest === null
            ? 'nobody travelling'
            : `fastest ${Math.round(stats.fastest * 3.6)} km/h`
        }
      />
      <StatTile
        label="Stationary"
        value={stats.stationary}
        tone="stopped"
        sub={
          stats.longestDwell === null
            ? 'no dwell recorded'
            : `longest ${formatDuration(stats.longestDwell)}`
        }
      />
      <StatTile
        label="Distance today"
        value={formatDistance(stats.distance)}
        sub="all employees"
      />
      <StatTile
        label="Needs attention"
        value={stats.attention}
        tone={stats.attention > 0 ? 'danger' : 'default'}
        sub={stats.attention > 0 ? 'tracking problems' : 'all trackers healthy'}
      />
      <StatTile
        label="Not reporting"
        value={stats.total - stats.reporting}
        tone={stats.total - stats.reporting > 0 ? 'warning' : 'default'}
        sub="no fix in 5 min"
      />
    </div>
  );
}
